import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle,
  Package,
  Home,
  CreditCard,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { useGuidedOrder } from '@/hooks/useGuidedOrders';
import { OrderLifecycleTracker } from '@/components/guided-order/OrderLifecycleTracker';

const formatCurrency = (cents: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
};

const StorefrontConfirmation: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useGuidedOrder(orderId ?? undefined);
  const order = data?.data;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const isGuidedFlow = order?.gate_mode === 'full' || order?.status === 'awaiting_intake' || order?.status === 'intake_completed' || order?.status === 'mdi_submitted' || order?.status === 'mdi_completed' || order?.status === 'rx_verified';

  if (!orderId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <p className="text-slate-600">No order ID in URL. Return home or use your receipt link.</p>
            <Button className="mt-4" onClick={() => navigate('/')}>
              <Home className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <Loader2 className="h-10 w-10 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-red-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-700 mb-4">
              <AlertCircle className="h-5 w-5" />
              <span>We couldn’t load this order. It may have been removed or the link is invalid.</span>
            </div>
            <Button variant="outline" onClick={() => navigate('/')}>
              <Home className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const meta = (order.metadata || {}) as Record<string, unknown>;
  const cardLast4 = meta.card_last4 as string | undefined;
  const cardBrand = meta.card_brand as string | undefined;
  const isSubscription = order.clinicProgramOption?.is_subscription === true;
  const subscriptionFrequency = order.clinicProgramOption?.subscription_frequency || order.subscription?.frequency || null;
  const frequencyLabel = (() => {
    if (!subscriptionFrequency) return 'recurring cycle';
    const f = subscriptionFrequency.toLowerCase();
    if (f.includes('week')) return 'weekly';
    if (f.includes('month')) return 'monthly';
    if (f.includes('quarter')) return 'quarterly';
    if (f.includes('year') || f.includes('annual')) return 'yearly';
    return subscriptionFrequency;
  })();

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Package className="h-8 w-8 text-green-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Order Confirmed</h1>
                <p className="text-xs text-gray-500">Thank you for your order</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate('/')} className="gap-2">
              <Home className="h-4 w-4" />
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 20 }}
          className="mb-8"
        >
          <Card className="max-w-2xl mx-auto border-green-200 bg-green-50/50">
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="h-12 w-12 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {isGuidedFlow ? 'Authorization Hold Placed' : 'Payment Successful!'}
                </h2>
                <p className="text-gray-600">
                  {isGuidedFlow
                    ? isSubscription
                      ? `Your card has been authorized. Your recurring ${frequencyLabel} program will begin once your provider verifies your prescription.`
                      : 'Your card has been authorized. Your card will be charged after your provider verifies your prescription.'
                    : 'Your order has been confirmed and will be processed shortly.'}
                </p>
                <div className="flex items-center justify-center gap-4 flex-wrap">
                  <Badge variant="outline" className="text-sm">
                    Order: {order.id}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <div className="lg:col-span-2 space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Order status</CardTitle>
                </CardHeader>
                <CardContent>
                  <OrderLifecycleTracker
                    status={order.status}
                    holdExpiresAt={order.hold_expires_at}
                  />
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-blue-600" />
                    Payment details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <p className="text-gray-600">
                    <span className="text-gray-500">Amount:</span>{' '}
                    <span className="font-medium">{formatCurrency(order.amount_cents)}</span>
                  </p>
                  {cardBrand && (
                    <p className="text-gray-600">
                      <span className="text-gray-500">Card:</span>{' '}
                      {cardBrand}{cardLast4 ? ` •••• ${cardLast4}` : ''}
                    </p>
                  )}
                  {order.hold_placed_at && (
                    <p className="text-gray-600">
                      Hold placed: {new Date(order.hold_placed_at).toLocaleString()}
                    </p>
                  )}
                  {order.hold_expires_at && (
                    <p className="text-gray-600">
                      Hold expires: {new Date(order.hold_expires_at).toLocaleString()}
                    </p>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {order.patient && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Patient</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm text-gray-600">
                    <p className="font-medium text-gray-900">
                      {order.patient.first_name} {order.patient.last_name}
                    </p>
                    {order.patient.email && (
                      <p className="mt-1">{order.patient.email}</p>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>

          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="sticky top-4"
            >
              <Card className="bg-blue-50 border-blue-200">
                <CardHeader>
                  <CardTitle className="text-lg">What’s next?</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                      <span>Confirmation sent to your email</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                      <span>Complete enrollment if required</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                      <span>Provider will review and verify your prescription</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                      <span>
                        {isSubscription
                          ? `Your recurring ${frequencyLabel} subscription will begin after verification`
                          : 'Your card will be charged after verification'}
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                      <span>
                        {isSubscription
                          ? `You will be billed on a ${frequencyLabel} cycle moving forward; medications ship each cycle with tracking emailed`
                          : 'Medications will be shipped; tracking will be emailed'}
                      </span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StorefrontConfirmation;
